/**
 * fingerprint — porta de `getGepIp` [application.js:14791], da classe `FPeMiner`
 * [14847-15250] e do `keygen` [920]. Gera fingerprint + geo do dispositivo QUANDO a
 * extensão não fornece esses dados no getSync (sem extensão, o signup ia sem eles, e o
 * backend usa fingerprint/geo no anti-abuso de período de teste).
 *
 * Regra do getHash [15202]:
 *  - fingerprint + sync.country já presentes → usa o fingerprint como está (sem geo).
 *  - fingerprint presente (sem country) → mantém o fingerprint e busca o geo.
 *  - sem fingerprint → SHA-256 dos sinais do navegador (os/formfields/language/browser/
 *    canvas/timezone/useragent) + geo/country; falha → keygen (64 chars aleatórios).
 * O fingerprint é persistido em cookie/localStorage `fp` e reusado (como o legado).
 */
import { geoipUrls } from '../config/env';
import { getCookie, setCookie } from './cookies';
import { setSync } from './extensionBridge';

export interface GeoData {
  ip?: string;
  city?: string;
  region?: string;
  country?: string;
  countryName?: string;
  geo?: string;
  timezone?: string;
}

/** Busca geo por IP com 3 serviços em cascata (getGepIp). Nunca rejeita — {} no pior caso. */
export async function getGeoIp(): Promise<GeoData> {
  const tryFetch = async (url: string): Promise<Record<string, unknown> | null> => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      return (await res.json()) as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  // 1) geojs.io
  const d1 = await tryFetch(geoipUrls.primary);
  if (d1) {
    const out: GeoData = {};
    if (d1['ip']) out.ip = String(d1['ip']);
    if (d1['city']) out.city = String(d1['city']);
    if (d1['region']) out.region = String(d1['region']);
    if (d1['country_code']) out.country = String(d1['country_code']);
    if (d1['country']) out.countryName = String(d1['country']);
    if (d1['latitude'] && d1['longitude']) out.geo = `${d1['latitude']},${d1['longitude']}`;
    if (d1['timezone']) out.timezone = String(d1['timezone']);
    return out;
  }

  // 2) ipleak.net (city_name/region_name/country_name/time_zone)
  const d2 = await tryFetch(geoipUrls.backup1);
  if (d2) {
    const out: GeoData = {};
    if (d2['ip']) out.ip = String(d2['ip']);
    if (d2['city_name']) out.city = String(d2['city_name']);
    if (d2['region_name']) out.region = String(d2['region_name']);
    if (d2['country_code']) out.country = String(d2['country_code']);
    if (d2['country_name']) out.countryName = String(d2['country_name']);
    if (d2['latitude'] && d2['longitude']) out.geo = `${d2['latitude']},${d2['longitude']}`;
    if (d2['time_zone']) out.timezone = String(d2['time_zone']);
    return out;
  }

  // 3) ipwhois.app
  const d3 = await tryFetch(geoipUrls.backup2);
  if (d3) {
    const out: GeoData = {};
    if (d3['ip']) out.ip = String(d3['ip']);
    if (d3['city']) out.city = String(d3['city']);
    if (d3['region']) out.region = String(d3['region']);
    if (d3['country_code']) out.country = String(d3['country_code']);
    if (d3['country']) out.countryName = String(d3['country']);
    if (d3['latitude'] && d3['longitude']) out.geo = `${d3['latitude']},${d3['longitude']}`;
    if (d3['timezone']) out.timezone = String(d3['timezone']);
    return out;
  }

  return {};
}

/* ---------- Sinais do navegador (FPeMiner) ---------- */

function fpUserAgent(): string {
  const ua = navigator.userAgent.toLowerCase();
  return `${ua}|${navigator.platform}|${navigator.language}`;
}

function fpTimezone(): string {
  try {
    return String((new Date().getTimezoneOffset() / 60) * -1);
  } catch {
    return 'Error';
  }
}

function fpLanguage(): string {
  try {
    return [navigator.language, (navigator as { userLanguage?: string }).userLanguage ?? '']
      .filter(Boolean)
      .join('|');
  } catch {
    return 'Error';
  }
}

function fpBrowser(): string {
  const ua = navigator.userAgent.toLowerCase();
  const w = window as Window & { chrome?: unknown; opr?: unknown; brave?: unknown };
  if (w.chrome && typeof w.opr !== 'undefined') return 'Opera';
  if (w.chrome && typeof w.brave !== 'undefined') return 'Brave';
  if (/edg\//.test(ua)) return 'Edge';
  if (w.chrome) return 'Chrome';
  if (/firefox|fxios/.test(ua)) return 'Firefox';
  if (/safari/.test(ua)) return 'Safari';
  return 'Unknown';
}

function fpOs(): string {
  const platform = navigator.platform.toLowerCase();
  if (platform.startsWith('win')) return 'Windows';
  if (platform.startsWith('linux')) return `Linux ${platform}`;
  if (platform.startsWith('mac')) return 'macOS';
  if (/iphone|ipad|ipod/.test(platform)) return 'iOS';
  if (/android/.test(navigator.userAgent.toLowerCase())) return 'Android';
  return 'Unknown';
}

/** Canvas fingerprint (porta de fingerprint_canvas): desenha texto e retorna o dataURL. */
function fpCanvas(): string {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'Error';
    const text = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ`~1!2@3#4$5%6^7&8*9(0)-_=+[{]}|;:',<.>/?";
    ctx.textBaseline = 'top';
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText(text, 2, 15);
    return canvas.toDataURL();
  } catch {
    return 'Error';
  }
}

function fpFormfields(): string {
  try {
    return `url=${window.location.href}|forms=${document.getElementsByTagName('form').length}`;
  } catch {
    return 'Error';
  }
}

/** keygen [920]: 64 chars alfanuméricos aleatórios (fallback quando o hash falha). */
export function keygen(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 64; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

/** SHA-256 hex dos sinais do navegador + geo (porta do ramo "sem fingerprint" do getHash). */
async function computeFingerprint(email: string | undefined, geo: GeoData): Promise<string> {
  try {
    const parts: string[] = [];
    if (email) parts.push(email);
    parts.push(fpOs(), fpFormfields(), fpLanguage(), fpBrowser(), fpCanvas(), fpTimezone(), fpUserAgent());
    if (geo.geo) parts.push(geo.geo);
    if (geo.country) parts.push(geo.country);
    const data = new TextEncoder().encode(parts.join('|'));
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return keygen();
  }
}

export interface Identity {
  fingerprint: string;
  geo: GeoData;
}

/**
 * resolveIdentity — porta do getHash [15202]. Recebe o que a extensão já forneceu
 * (fingerprint/country do getSync) e completa o que faltar:
 *  - fingerprint + country presentes → nada a buscar (usa o fingerprint, sem geo).
 *  - senão → busca o geo; reusa o fp persistido (cookie/localStorage) ou calcula um novo;
 *    persiste o fp para as próximas vezes.
 */
export async function resolveIdentity(
  syncFingerprint: string | undefined,
  syncCountry: string | undefined,
  email: string | undefined,
): Promise<Identity> {
  // Extensão já entregou tudo: usa como está.
  if (syncFingerprint && syncCountry) {
    return { fingerprint: syncFingerprint, geo: {} };
  }

  const geo = await getGeoIp();

  // fp persistido tem precedência (legado: localStorage/cookie "fp").
  let fingerprint = syncFingerprint;
  const stored = getStoredFp();
  if (stored) fingerprint = stored;
  if (!fingerprint) fingerprint = await computeFingerprint(email, geo);

  persistFp(fingerprint);
  return { fingerprint, geo };
}

function getStoredFp(): string | undefined {
  try {
    return localStorage.getItem('fp') ?? getCookie('fp') ?? undefined;
  } catch {
    return getCookie('fp') ?? undefined;
  }
}

/** Fingerprint persistido (localStorage/cookie `fp`), leitura SÍNCRONA — usado pelo
 * requestContext para injetar o header `fingerprint` em toda request (legado [1506/1573]). */
export function getStoredFingerprint(): string | undefined {
  return getStoredFp();
}

/**
 * Persiste o fingerprint nos TRÊS lugares que o consomem — e o `chrome.storage.sync` é o
 * que destrava a captura.
 *
 * O content script gateia a captura inteira em `sync.fingerprint` [content.js:380]. Ele até
 * copia `localStorage.fp` → sync [content.js:472], mas só quando roda o bloco da página do
 * painel: num perfil novo, esse bloco chega ANTES de o painel ter gerado o fingerprint, o
 * sync fica sem a chave e a captura nunca inicia. Gravar aqui — no ponto único em que o
 * fingerprint passa a existir — fecha essa corrida para todos os caminhos (boot e cadastro).
 *
 * Sem extensão instalada, `setSync` é uma mensagem que ninguém escuta: inofensiva.
 */
function persistFp(fingerprint: string): void {
  try {
    localStorage.setItem('fp', fingerprint);
  } catch {
    /* storage indisponível */
  }
  setCookie('fp', fingerprint);
  try {
    setSync({ fingerprint });
  } catch {
    /* extensão ausente ou bridge indisponível — os outros dois destinos já foram gravados */
  }
}
